(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const SIDE_GUTTER = 48;
  const configs = [
    {
      section: "#MainContent > .shopify-section[id*='__testimonials_6Q4RJk']",
      track: 'testimonials-component',
      items: '.testimonials__items',
      loop: true,
    },
    {
      section: "#MainContent > .shopify-section[id*='__testimonials_8JHr3L']",
      track: 'testimonials-component',
      items: '.testimonials__items',
      loop: true,
    },
    {
      section: "#MainContent > .shopify-section[id*='__featured_blog_L8j4kR']",
      track: '.featured-blog__wrapper',
      items: '.featured-blog__items',
      loop: true,
    },
  ];

  const chevron = (direction) => `
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="${direction === 'prev' ? 'M12.75 16.25 6.5 10l6.25-6.25' : 'M7.25 3.75 13.5 10l-6.25 6.25'}"
        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`;

  function centreScrollLeft(track, item) {
    return item.offsetLeft + item.offsetWidth / 2 - track.clientWidth / 2;
  }

  function closestItem(track, items) {
    const centre = track.scrollLeft + track.clientWidth / 2;
    return items.reduce((closest, item) => {
      const itemCentre = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCentre - centre);
      return !closest || distance < closest.distance ? { item, distance } : closest;
    }, null)?.item;
  }

  function createArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `au-mobile-carousel-arrow au-mobile-carousel-arrow--${direction}`;
    button.setAttribute('aria-label', direction === 'prev' ? 'Previous item' : 'Next item');
    button.innerHTML = chevron(direction);
    return button;
  }

  function setup(config) {
    const section = document.querySelector(config.section);
    const track = section?.querySelector(config.track);
    const items = track?.querySelector(config.items);
    if (!section || !track || !items || track.dataset.auMobileCarousel === 'true') return;

    const originals = Array.from(items.children);
    if (originals.length < 2) return;

    section.classList.add('au-mobile-carousel-section');
    track.classList.add('au-mobile-carousel-track');
    items.classList.add('au-mobile-carousel-items');
    track.dataset.auMobileCarousel = 'true';

    let firstClone;
    let lastClone;
    if (config.loop) {
      firstClone = originals[0].cloneNode(true);
      lastClone = originals[originals.length - 1].cloneNode(true);
      firstClone.classList.add('au-mobile-carousel-clone');
      lastClone.classList.add('au-mobile-carousel-clone');
      firstClone.dataset.auClone = 'first';
      lastClone.dataset.auClone = 'last';
      firstClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('aria-hidden', 'true');
      items.insertBefore(lastClone, originals[0]);
      items.appendChild(firstClone);
    }

    const prev = createArrow('prev');
    const next = createArrow('next');
    section.append(prev, next);

    let scrollTimer;
    let resizeTimer;
    let normalising = false;

    const setCardWidth = () => {
      const width = Math.max(220, track.clientWidth - SIDE_GUTTER * 2);
      track.style.setProperty('--au-mobile-card-width', `${width}px`);
    };

    const positionArrows = () => {
      const sectionRect = section.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const top = trackRect.top - sectionRect.top + Math.min(trackRect.height, originals[0].offsetHeight) / 2;
      prev.style.top = `${top}px`;
      next.style.top = `${top}px`;
    };

    const updateButtons = () => {
      if (config.loop) {
        prev.disabled = false;
        next.disabled = false;
        return;
      }
      const current = closestItem(track, originals);
      const index = Math.max(0, originals.indexOf(current));
      prev.disabled = index === 0;
      next.disabled = index === originals.length - 1;
    };

    const jumpTo = (item, smooth = true) => {
      track.scrollTo({
        left: centreScrollLeft(track, item),
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
    };

    // Move from an edge clone to its matching real card without animating the
    // whole track backwards. Temporarily disabling snap and smooth scrolling
    // makes the hand-off visually seamless in both directions.
    const jumpToLoopMatch = (item) => {
      normalising = true;
      const previousBehavior = track.style.scrollBehavior;
      const previousSnap = track.style.scrollSnapType;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollLeft = centreScrollLeft(track, item);
      track.getBoundingClientRect();
      requestAnimationFrame(() => {
        track.style.scrollBehavior = previousBehavior;
        track.style.scrollSnapType = previousSnap;
        requestAnimationFrame(() => { normalising = false; });
      });
    };

    const normaliseLoop = () => {
      if (!config.loop || normalising) return;
      const all = Array.from(items.children);
      const current = closestItem(track, all);
      if (current === firstClone) {
        jumpToLoopMatch(originals[0]);
      } else if (current === lastClone) {
        jumpToLoopMatch(originals[originals.length - 1]);
      }
    };

    const onScrollEnd = () => {
      normaliseLoop();
      updateButtons();
    };

    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(onScrollEnd, 100);
    }, { passive: true });

    const move = (delta) => {
      const all = Array.from(items.children);
      const current = closestItem(track, all);
      const index = all.indexOf(current);
      const target = all[Math.max(0, Math.min(all.length - 1, index + delta))];
      if (target) jumpTo(target);
    };

    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    const refresh = () => {
      setCardWidth();
      positionArrows();
      const current = config.loop ? originals[0] : (closestItem(track, originals) || originals[0]);
      jumpTo(current, false);
      updateButtons();
    };

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refresh, 100);
    };

    window.addEventListener('resize', onResize, { passive: true });

    track._auMobileCarouselCleanup = () => {
      clearTimeout(scrollTimer);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      firstClone?.remove();
      lastClone?.remove();
      prev.remove();
      next.remove();
      track.classList.remove('au-mobile-carousel-track');
      items.classList.remove('au-mobile-carousel-items');
      section.classList.remove('au-mobile-carousel-section');
      track.style.removeProperty('--au-mobile-card-width');
      delete track.dataset.auMobileCarousel;
    };

    requestAnimationFrame(() => requestAnimationFrame(refresh));
  }

  function destroy(config) {
    const track = document.querySelector(config.section)?.querySelector(config.track);
    track?._auMobileCarouselCleanup?.();
  }

  function sync() {
    configs.forEach(window.matchMedia(MOBILE_QUERY).matches ? setup : destroy);
  }

  document.addEventListener('DOMContentLoaded', sync);
  document.addEventListener('shopify:section:load', sync);
  window.matchMedia(MOBILE_QUERY).addEventListener('change', sync);
})();
